import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl, useContent } from "@/lib/content";
import { Reveal } from "./Reveal";

export const useWork = () => useQuery({ queryKey: ["work"], queryFn: async () => (await api.get("/work")).data, staleTime: 60_000 });

export const useAutoScroll = (ref, ready) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !ready || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf, timer, paused = false, pos = el.scrollLeft;
    const half = () => el.scrollWidth / 2;
    const step = () => {
      if (!paused) {
        pos += 0.45;
        if (pos >= half()) pos -= half();
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    const pause = () => { paused = true; clearTimeout(timer); };
    const resume = () => { clearTimeout(timer); timer = setTimeout(() => { pos = el.scrollLeft; paused = false; }, 2500); };
    const wrap = () => {
      if (!paused) return;
      if (el.scrollLeft >= half()) el.scrollLeft -= half();
      else if (el.scrollLeft <= 0) el.scrollLeft += half();
    };
    const evs = [["pointerenter", pause], ["pointerdown", pause], ["touchstart", pause], ["pointerleave", resume], ["touchend", resume], ["scroll", wrap]];
    evs.forEach(([e, f]) => el.addEventListener(e, f, { passive: true }));
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); evs.forEach(([e, f]) => el.removeEventListener(e, f)); };
  }, [ref, ready]);
};

export const WorkStrip = ({ photos, onOpen, testId = "work-strip" }) => {
  const ref = useRef(null);
  useAutoScroll(ref, photos.length > 0);
  const nudge = (dir) => {
    const el = ref.current;
    el.dispatchEvent(new Event("pointerdown"));
    el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
    el.dispatchEvent(new Event("pointerleave"));
  };
  const loop = [...photos, ...photos];
  return (
    <div className="work-strip-wrap" data-testid={testId}>
      <button type="button" className="work-arrow left" onClick={() => nudge(-1)} aria-label="Previous photos" data-testid={`${testId}-prev`}><ChevronLeft size={20} /></button>
      <div className="work-strip" ref={ref}>
        {loop.map((p, i) => (
          <button type="button" key={`${p.id}-${i}`} className="work-tile" onClick={() => onOpen(i % photos.length)} aria-label={p.caption || `Open photo ${(i % photos.length) + 1}`}
            tabIndex={i >= photos.length ? -1 : 0} data-testid={i < photos.length ? `work-tile-${i}` : undefined}>
            <img src={imgUrl(p.url)} alt={p.caption || "Wax Moi client result"} loading="lazy" decoding="async" draggable={false} />
          </button>
        ))}
      </div>
      <button type="button" className="work-arrow right" onClick={() => nudge(1)} aria-label="Next photos" data-testid={`${testId}-next`}><ChevronRight size={20} /></button>
    </div>
  );
};

export const OurWork = () => {
  const w = useContent().data.work;
  const { data } = useWork();
  if (!w || !data?.length) return null;
  return (
    <section className="section work" id="work" data-testid="work-section">
      <div className="wrap">
        <Reveal>
          <p className="eyebrow">{w.eyebrow}</p>
          <h2 className="team-title">{w.title}</h2>
          {w.text && <p className="team-lead">{w.text}</p>}
        </Reveal>
      </div>
      <WorkStrip photos={data} onOpen={(i) => { window.location.href = `/our-work?photo=${i}`; }} />
      <div className="wrap work-cta">
        <Link to="/our-work" className="btn gold" data-testid="work-see-all-link">{w.link_label || "See all our work"} <ArrowUpRight size={14} /></Link>
      </div>
    </section>
  );
};
